"use client"

import type React from "react"

import { useEffect, useState } from "react"
import type { ILeadership } from "../../../../shared/models/three-sixty-feedback-models/Leadership"
import type { IValue } from "../../../../shared/models/three-sixty-feedback-models/Values"
import { useAppContext } from "../../../../shared/functions/Context"
import {
  type IValueRating,
  defaultValueRating,
} from "../../../../shared/models/three-sixty-feedback-models/ValueRating"
import {
  type ILeadershipRating,
  defaultLeadershipRating,
} from "../../../../shared/models/three-sixty-feedback-models/LeadershipRating"
import showModalFromId, { hideModalFromId } from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import SavedAlertModal from "../../shared-components/SavedAlertModal"
import Modal from "../../../../shared/components/Modal"
import type { IRateAssignment } from "../../../../shared/models/three-sixty-feedback-models/RateAssignments"
import { updateRatedUsersPerAssignmentLeadership, updateRatedUsersPerAssignmentValues } from "../../ratings/functions"
import { defaultUser, type IUser } from "../../../../shared/models/User"
import {
  Play,
  Square,
  RotateCcw,
  MessageSquare,
  Info,
  CheckCircle2,
  AlertTriangle,
  Send,
  Loader2,
  Star,
  Crown,
  Heart,
} from "lucide-react"

interface IProps {
  value: (IValue | ILeadership)[]
  ratee?: string
  rater?: string
  description: string
  disableAfterSubmission: boolean
  setRatees: React.Dispatch<React.SetStateAction<IUser>>
}

export const UserQuestionnaireBox: React.FC<IProps> = ({ value, ratee, rater, description, setRatees }) => {
  const { api, store } = useAppContext()
  const myUid = store.auth.meJson?.uid
  const user = store.user.all.find((d) => d.asJson.uid === ratee)
  const me = rater !== undefined && rater !== "" ? rater : (myUid ?? "")

  const [startValue, setStartValue] = useState("")
  const [stopValue, setStopValue] = useState("")
  const [continueValue, setContinueValue] = useState("")
  const [additionalComment, setAdditionalComment] = useState("")
  const [loading, setLoading] = useState<boolean>(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [leadershipRating, setLeadershipRating] = useState<ILeadershipRating>({
    ...defaultLeadershipRating,
  })
  const [valueRating, setValueRating] = useState<IValueRating>({
    ...defaultValueRating,
  })
  const [isCheckedMap, setIsCheckedMap] = useState<Record<string, Record<number, boolean>>>({})

  const ratingAssignments = store.ratingAssignments.all.find((r) => r.asJson.isActive === true)?.asJson

  function isValue(item: IValue | ILeadership): item is IValue {
    return "valueName" in item
  }

  const updateRatingAssignment = async (ratingAssignment: IRateAssignment) => {
    await api.ratingAssignments.update(ratingAssignment)
  }

  const onSubmit = async () => {
    try {
      setLoading(true)
      const requiredFieldsFilled =
        startValue.trim() !== "" &&
        stopValue.trim() !== "" &&
        continueValue.trim() !== "" &&
        Object.values(isCheckedMap).every((map) => {
          const checkedCount = Object.values(map).filter((isChecked) => isChecked).length
          return checkedCount === 1
        })

      if (!requiredFieldsFilled) {
        setShowNotification(true)
        setTimeout(() => setShowNotification(false), 5000)
        return
      }

      let updatedRating

      if (value.some(isValue)) {
        updatedRating = prepareValueRating()
        const newRating: IValueRating = { ...updatedRating }
        if (ratee && description) {
          await api.valueRating.create(newRating, ratee, description)
        }
        if (updatedRating && ratingAssignments !== undefined) {
          const updateAlreadyrated = updateRatedUsersPerAssignmentValues(me ?? "", ratee ?? "", ratingAssignments)
          updateRatingAssignment(updateAlreadyrated)
        }
        handleSuccessfulSubmission()
      } else {
        updatedRating = prepareLeadershipRating()
        if (ratee && description) {
          await api.leadershipRating.create(updatedRating, ratee ?? "", description ?? "")
        }
        if (updatedRating && ratingAssignments !== undefined) {
          const updateAlreadyrated = updateRatedUsersPerAssignmentLeadership(me ?? "", ratee ?? "", ratingAssignments)
          updateRatingAssignment(updateAlreadyrated)
        }
        handleSuccessfulSubmission()
      }
      setRatees(defaultUser)
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }

  const checkFormValidity = () => {
    const allFieldsFilled = startValue.trim() !== "" && stopValue.trim() !== "" && continueValue.trim() !== ""
    const allCheckboxesFilled = value.every((item, valueIndex) => {
      const statements = isValue(item) ? item.statements : (item as ILeadership).statements
      return statements.every((_, statementIndex) => {
        const setKey = `${valueIndex}-${statementIndex}`
        return Object.values(isCheckedMap[setKey] || {}).some((isChecked) => isChecked)
      })
    })
    setIsFormValid(allFieldsFilled && allCheckboxesFilled)
  }

  useEffect(() => {
    checkFormValidity()
  }, [startValue, stopValue, continueValue, isCheckedMap, value])

  const prepareValueRating = () => {
    const updatedValueRating: IValueRating = { ...valueRating }
    updatedValueRating.date = Date.now()
    updatedValueRating.rateeId = ratee ?? ""
    updatedValueRating.dimension = "Values"
    updatedValueRating.raterId = myUid
    updatedValueRating.department = user?.asJson.department ?? ""
   updatedValueRating.status="Completed"
    updatedValueRating.values = {
      [me ?? ""]: {
        ...updatedValueRating.values[me ?? ""],
        additionalComment,
        stopDoing: stopValue,
        startDoing: startValue,
        continueDoing: continueValue,
        ratings: { ...updatedValueRating.values[me ?? ""]?.ratings },
      },
    }
    return updatedValueRating
  }

  const prepareLeadershipRating = () => {
    const updatedLeadershipRating = { ...leadershipRating }
    updatedLeadershipRating.date = Date.now()
    updatedLeadershipRating.rateeId = ratee ?? ""
    updatedLeadershipRating.dimension = "Leadership"
     updatedLeadershipRating.status="Completed"
    updatedLeadershipRating.raterId = me
    updatedLeadershipRating.criteria = {
      [me ?? ""]: {
        additionalComment,
        stopDoing: stopValue,
        startDoing: startValue,
        continueDoing: continueValue,
        ratings: { ...updatedLeadershipRating.criteria[me ?? ""]?.ratings },
      },
    }
    return updatedLeadershipRating
  }

  const handleSuccessfulSubmission = () => {
    setIsCheckedMap({})
    setStopValue("")
    setStartValue("")
    setContinueValue("")
    setAdditionalComment("")
    setValueRating(defaultValueRating)
    setLeadershipRating(defaultLeadershipRating)
    showSuccessModal()
  }

  const showSuccessModal = () => {
    showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_SUBMITTED_MODAL)
    setTimeout(() => {
      hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_SUBMITTED_MODAL)
    }, 3000)
  }

  const handleError = (error: any) => {
    console.error(error)
    if (error.response) {
      const statusCode = error.response.status
      const errorMessage = error.response.data.message || "Unknown error"
      alert(`Submission failed. Error (${statusCode}): ${errorMessage}`)
    } else if (error.request) {
      alert("Network error. Please check your internet connection and try again.")
    } else {
      alert("Fill in all required fields")
    }
  }

  const handleCheckboxChange = (
    valueIndex: number,
    statementIndex: number,
    ratingIndex: number,
    isChecked: boolean,
  ) => {
    setIsCheckedMap((prevState) => {
      const setKey = `${valueIndex}-${statementIndex}`
      const newState = { ...(prevState || {}) }
      newState[setKey] = newState[setKey] || {}

      // Uncheck all checkboxes in the same statement
      for (let i = 0; i < 5; i++) {
        newState[setKey][i] = false
      }

      // Check the clicked checkbox
      newState[setKey][ratingIndex] = isChecked
      return newState
    })

    const currentItem = value[valueIndex]
    if ("valueName" in currentItem) {
      const valueItem = currentItem as IValue
      setValueRating((prevRating) => ({
        ...prevRating,
        values: {
          ...prevRating.values,
          [me ?? ""]: {
            additionalComment: additionalComment,
            stopDoing: stopValue,
            startDoing: startValue,
            continueDoing: continueValue,
            ratings: {
              ...prevRating.values[me ?? ""]?.ratings,
              [valueItem.valueName]: {
                ...prevRating.values[me ?? ""]?.ratings?.[valueItem.valueName],
                [valueItem.statements[statementIndex].statement]: isChecked ? ratingIndex + 1 : 0,
              },
            },
          },
        },
      }))
    } else {
      const leadershipItem = currentItem as ILeadership
      setLeadershipRating((prevRating) => ({
        ...prevRating,
        criteria: {
          ...prevRating.criteria,
          [me ?? ""]: {
            additionalComment: additionalComment,
            stopDoing: stopValue,
            startDoing: startValue,
            continueDoing: continueValue,
            ratings: {
              ...prevRating.criteria[me ?? ""]?.ratings,
              [leadershipItem.criteriaName]: {
                ...prevRating.criteria[me ?? ""]?.ratings?.[leadershipItem.criteriaName],
                [leadershipItem.statements[statementIndex].statement]: isChecked ? ratingIndex + 1 : 0,
              },
            },
          },
        },
      }))
    }
    checkFormValidity()
  }

  useEffect(() => {
    const load = async () => {
      await api.ratingAssignments.getAll()
      await api.user.getAll()
    }
    load()
  }, [api.ratingAssignments, api.user])

  const getRatingColor = (index: number) => {
    const colors = [
      "from-red-500 to-red-600", // Very Bad
      "from-orange-500 to-orange-600", // Bad
      "from-yellow-500 to-yellow-600", // Better
      "from-blue-500 to-blue-600", // Good
      "from-green-500 to-green-600", // Excellent
    ]
    return colors[index]
  }

  const getRatingLabel = (index: number) => {
    const labels = ["Very Bad", "Bad", "Better", "Good", "Excellent"]
    return labels[index]
  }

  const getItemIcon = (item: IValue | ILeadership) => {
    if (isValue(item)) {
      return <Heart className="h-5 w-5 text-blue-600" />
    }
    return <Crown className="h-5 w-5 text-purple-600" />
  }

  const getItemGradient = (item: IValue | ILeadership) => {
    if (isValue(item)) {
      return "from-blue-50 to-indigo-50"
    }
    return "from-purple-50 to-violet-50"
  }

  const getItemBorder = (item: IValue | ILeadership) => {
    if (isValue(item)) {
      return "border-blue-200"
    }
    return "border-purple-200"
  }

  if (!ratee) {
    return (
      <div className="text-center py-12">
        <div className="p-4 bg-slate-100 rounded-full w-fit mx-auto mb-4">
          <Star className="h-12 w-12 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Select a User to Rate</h3>
        <p className="text-slate-600">Please select a user from the dropdown above to begin the assessment.</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Assessment Items */}
      {value.map((item, index) => (
        <div
          key={index}
          className={`bg-gradient-to-br ${getItemGradient(item)} rounded-2xl border ${getItemBorder(
            item,
          )} shadow-lg overflow-hidden`}
        >
          {/* Item Header */}
          <div className="bg-white/60 backdrop-blur-sm p-6 border-b border-white/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">{getItemIcon(item)}</div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  {isValue(item) ? item.valueName : item.criteriaName}
                </h3>
                <p className="text-sm text-slate-600">
                  {isValue(item) ? "Core organizational value" : "Leadership competency"}
                </p>
              </div>
            </div>
          </div>

          {/* Statements */}
          <div className="p-6 space-y-6">
            {(isValue(item) ? item.statements : (item as ILeadership).statements).map((statement, statementIndex) => (
              <div key={statementIndex} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm">
                <div className="mb-6">
                  <p className="text-lg font-medium text-slate-800 leading-relaxed">
                    {statement.statement}
                    <span className="text-red-500 ml-1">*</span>
                  </p>
                </div>

                {/* Rating Options */}
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                  {Array.from({ length: 5 }).map((_, ratingIndex) => {
                    const setKey = `${index}-${statementIndex}`
                    const isChecked = isCheckedMap[setKey]?.[ratingIndex] || false

                    return (
                      <label
                        key={ratingIndex}
                        className={`relative cursor-pointer group transition-all duration-300 ${
                          isChecked ? "scale-105" : "hover:scale-102"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          onChange={(e) => handleCheckboxChange(index, statementIndex, ratingIndex, e.target.checked)}
                          checked={isChecked}
                          value={`${ratingIndex + 1}`}
                          required
                        />

                        <div
                          className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                            isChecked
                              ? `bg-gradient-to-br ${getRatingColor(
                                  ratingIndex,
                                )} border-transparent text-white shadow-lg`
                              : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-md"
                          }`}
                        >
                          {/* Rating Number */}
                          <div className="text-center mb-2">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mx-auto ${
                                isChecked ? "bg-white/20" : "bg-slate-100"
                              }`}
                            >
                              {ratingIndex + 1}
                            </div>
                          </div>

                          {/* Rating Label */}
                          <div className="text-center">
                            <span className={`text-sm font-semibold ${isChecked ? "text-white" : "text-slate-700"}`}>
                              {getRatingLabel(ratingIndex)}
                            </span>
                          </div>

                          {/* Check Icon */}
                          {isChecked && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle2 className="h-5 w-5 text-white" />
                            </div>
                          )}

                          {/* Hover Effect */}
                          <div
                            className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                              isChecked
                                ? "opacity-0"
                                : "opacity-0 group-hover:opacity-10 bg-gradient-to-br " + getRatingColor(ratingIndex)
                            }`}
                          />
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Feedback Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <MessageSquare className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Feedback Comments</h3>
            <p className="text-sm text-slate-600">Provide specific, actionable feedback</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Start Doing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Play className="h-4 w-4 text-green-600" />
              <label className="text-sm font-semibold text-slate-800">
                Start Doing <span className="text-red-500">*</span>
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  What behaviors should they start doing?
                </div>
              </div>
            </div>
            <textarea
              className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-all duration-200"
              value={startValue}
              maxLength={200}
              rows={4}
              placeholder="What should they start doing to improve performance?"
              onChange={(e) => setStartValue(e.target.value)}
              required
            />
            <div className="text-xs text-slate-500 text-right">{startValue.length}/200</div>
          </div>

          {/* Stop Doing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Square className="h-4 w-4 text-red-600" />
              <label className="text-sm font-semibold text-slate-800">
                Stop Doing <span className="text-red-500">*</span>
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  What behaviors should they stop doing?
                </div>
              </div>
            </div>
            <textarea
              className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all duration-200"
              value={stopValue}
              maxLength={200}
              rows={4}
              placeholder="What should they stop doing that hinders performance?"
              onChange={(e) => setStopValue(e.target.value)}
              required
            />
            <div className="text-xs text-slate-500 text-right">{stopValue.length}/200</div>
          </div>

          {/* Continue Doing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-blue-600" />
              <label className="text-sm font-semibold text-slate-800">
                Continue Doing <span className="text-red-500">*</span>
              </label>
              <div className="group relative">
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                  What behaviors should they continue doing?
                </div>
              </div>
            </div>
            <textarea
              className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
              value={continueValue}
              maxLength={200}
              rows={4}
              placeholder="What should they continue doing that contributes positively?"
              onChange={(e) => setContinueValue(e.target.value)}
              required
            />
            <div className="text-xs text-slate-500 text-right">{continueValue.length}/200</div>
          </div>
        </div>

        {/* Additional Comments */}
        <div className="mt-6 space-y-3">
          <label className="text-sm font-semibold text-slate-800">Additional Comments (Optional)</label>
          <textarea
            className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
            value={additionalComment}
            maxLength={500}
            rows={4}
            placeholder="Any additional feedback or context you'd like to provide..."
            onChange={(e) => setAdditionalComment(e.target.value)}
          />
          <div className="text-xs text-slate-500 text-right">{additionalComment.length}/500</div>
        </div>
      </div>

      {/* Notification */}
      {showNotification && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 animate-fadeIn">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm">
            Please fill in all required fields and select exactly one rating for each statement.
          </p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-center">
        <button
          onClick={onSubmit}
          disabled={!isFormValid || ratee === null || ratee === "" || loading}
          className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
            isFormValid && ratee && !loading
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl hover:scale-105"
              : "bg-slate-300 text-slate-500 cursor-not-allowed"
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              Submit Assessment
            </>
          )}
        </button>
      </div>

      {/* Modal */}
      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_SUBMITTED_MODAL}>
        <SavedAlertModal />
      </Modal>
    </div>
  )
}

export default UserQuestionnaireBox
